import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";

import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { apiService } from "../../services/api";

const predefinedColors = [
  "#3B82F6", // Blue
  "#10B981", // Green
  "#F59E0B", // Yellow
  "#EF4444", // Red
  "#8B5CF6", // Purple
  "#F97316", // Orange
  "#06B6D4", // Cyan
  "#84CC16", // Lime
];

const SkillCategoryForm = ({
  clubId,
  editingCategory,
  onSuccess,
  onCancel,
}) => {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    color: "#3B82F6",
    icon: "academic-cap",
  });
  const [loading, setLoading] = useState(false);
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);

  const iconOptions = [
    { value: "academic-cap", label: "🎓 Academic Cap", icon: "🎓" },
    { value: "shield", label: "🛡️ Shield (Defense)", icon: "🛡️" },
    { value: "bolt", label: "⚡ Bolt (Speed)", icon: "⚡" },
    { value: "target", label: "🎯 Target (Accuracy)", icon: "🎯" },
    { value: "heart", label: "❤️ Heart (Conditioning)", icon: "❤️" },
    { value: "brain", label: "🧠 Brain (IQ)", icon: "🧠" },
    { value: "muscle", label: "💪 Muscle (Strength)", icon: "💪" },
    { value: "eye", label: "👁️ Eye (Vision)", icon: "👁️" },
  ];

  const isValidHexColor = (color) => {
    return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color);
  };

  useEffect(() => {
    if (editingCategory) {
      setFormData({
        name: editingCategory.name || "",
        description: editingCategory.description || "",
        color: editingCategory.color || "#3B82F6",
        icon: editingCategory.icon || "academic-cap",
      });

      // Check if the current color is predefined or custom
      const isPredefinedColor = predefinedColors.includes(
        editingCategory.color || "#3B82F6",
      );
      setShowCustomColorPicker(!isPredefinedColor);
    }
  }, [editingCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!isValidHexColor(formData.color)) {
      toast.error("Please enter a valid hex color code (e.g., #FF5733)");
      setLoading(false);
      return;
    }

    try {
      const submitData = {
        ...formData,
        club_id: clubId,
      };

      if (editingCategory) {
        await apiService.updateSkillCategory(editingCategory.id, submitData);
        toast.success("Category updated successfully!");
      } else {
        await apiService.createSkillCategory(submitData);
        toast.success("Category created successfully!");
      }

      onSuccess();
    } catch (error) {
      toast.error("Failed to save category");
    }
    setLoading(false);
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Input
        label="Category Name"
        placeholder="e.g., Offensive Skills, Defensive Skills, Athletic Skills"
        value={formData.name}
        onChange={(e) => handleInputChange("name", e.target.value)}
        required
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Description (Optional)
        </label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-slate-500 focus:border-slate-500"
          rows="3"
          placeholder="Describe what skills belong in this category..."
          value={formData.description}
          onChange={(e) => handleInputChange("description", e.target.value)}
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category Color
        </label>

        {!showCustomColorPicker ? (
          <div className="space-y-3">
            <div className="flex space-x-2">
              {predefinedColors.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`w-8 h-8 rounded-full border-2 ${
                    formData.color === color
                      ? "border-gray-900"
                      : "border-gray-300"
                  }`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleInputChange("color", color)}
                />
              ))}
            </div>
            <button
              type="button"
              className="text-sm text-slate-600 hover:text-slate-800 underline"
              onClick={() => setShowCustomColorPicker(true)}
            >
              Use custom color
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.color}
                onChange={(e) => handleInputChange("color", e.target.value)}
                className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
              />
              <Input
                placeholder="Enter hex color (e.g., #FF5733)"
                value={formData.color}
                onChange={(e) => handleInputChange("color", e.target.value)}
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
                className="flex-1"
              />
              <div
                className="w-10 h-10 rounded border border-gray-300"
                style={{
                  backgroundColor: isValidHexColor(formData.color)
                    ? formData.color
                    : "#cccccc",
                }}
                title="Color preview"
              />
            </div>
            <button
              type="button"
              className="text-sm text-slate-600 hover:text-slate-800 underline"
              onClick={() => {
                setShowCustomColorPicker(false);
                handleInputChange("color", "#3B82F6");
              }}
            >
              Use predefined colors
            </button>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Category Icon
        </label>
        <div className="grid grid-cols-2 gap-2">
          {iconOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`p-3 text-left border rounded-md hover:bg-gray-50 ${
                formData.icon === option.value
                  ? "border-slate-500 bg-slate-50"
                  : "border-gray-300"
              }`}
              onClick={() => handleInputChange("icon", option.value)}
            >
              <span className="text-lg mr-2">{option.icon}</span>
              <span className="text-sm">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-gray-50 border border-gray-200 rounded-md p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Preview</h4>
        <div className="flex items-center space-x-3">
          <div
            className="w-4 h-4 rounded-full"
            style={{ backgroundColor: formData.color }}
          />
          <span className="text-lg">
            {iconOptions.find((opt) => opt.value === formData.icon)?.icon}
          </span>
          <span className="font-medium">
            {formData.name || "Category Name"}
          </span>
        </div>
        {formData.description && (
          <p className="text-sm text-gray-600 mt-2">{formData.description}</p>
        )}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" disabled={loading}>
          {loading
            ? "Saving..."
            : editingCategory
              ? "Update Category"
              : "Create Category"}
        </Button>
      </div>
    </form>
  );
};

export default SkillCategoryForm;
